import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Divider } from 'antd';
import dayjs from 'dayjs';
import { OssEntity } from '@/service/driverArchive/define';
import type { UploadFile } from 'antd';
import DriverBasicInfoForm from './components/DriverBasicInfoForm';
import DriverCoreSkillsForm from './components/DriverCoreSkillsForm';
import DrivingSkillsTable from './components/DrivingSkillsTable';
import WorkHistorySection from './components/WorkHistorySection';
import SafetyAssessmentSection, {
  AssessmentDataItem,
} from './components/SafetyAssessmentSection';

interface iImgFileDisplay {
  idCard: UploadFile[];
  driverLicense: UploadFile[];
  photo: UploadFile[];
}

/**
 * 司机详情弹窗属性
 */
interface DriverDetailModalProps {
  visible: boolean; // 是否可见
  mode: 'view' | 'edit'; // 模式：查看或编辑
  onCancel: () => void; // 取消回调
  onOk: (values: any) => void; // 确认回调
  initialValues?: any; // 表单初始值
  confirmLoading?: boolean; // 确认按钮loading状态
}



/**
 * 司机详情弹窗组件
 * @param visible
 * @param mode
 * @param onCancel
 * @param onOk
 * @param initialValues
 * @param confirmLoading
 * @constructor
 */
const DriverDetailModal: React.FC<DriverDetailModalProps> = ({ visible, mode, onCancel, onOk, initialValues, confirmLoading }) => {
  const [form] = Form.useForm();
  const isViewMode = mode === 'view';
  const [fileLists, setFileLists] = useState<iImgFileDisplay>({
    idCard: [],
    driverLicense: [],
    photo: [],
  });
  const [drivingSkills, setDrivingSkills] = useState<any[]>([]);
  const [workHistory, setWorkHistory] = useState<any[]>([]);

  const [latestAssessmentLevel, setLatestAssessmentLevel] = useState('');

  const [individualInfo, setIndividualInfo] = useState<AssessmentDataItem[]>([
    { key: 'age', item: '年龄分布', details: '', score: 0, proportionScore: 0 },
    { key: 'drivingExperience', item: '驾龄分布', details: '', score: 0, proportionScore: 0 },
    { key: 'safetyAwareness', item: '安全意识', details: '', score: 0, proportionScore: 0 },
    { key: 'personality', item: '性格', details: '', score: 0, proportionScore: 0 },
    { key: 'communication', item: '沟通能力', details: '', score: 0, proportionScore: 0 },
  ]);

  const [drivingViolations, setDrivingViolations] = useState<AssessmentDataItem[]>([
    { key: 'speeding', item: '超速', details: '', score: 0, proportionScore: 0 },
    { key: 'handsOff', item: '方向盘脱手', details: '', score: 0, proportionScore: 0 },
    { key: 'noSeatbelt', item: '未系安全带', details: '', score: 0, proportionScore: 0 },
    { key: 'phoneCall', item: '接打电话', details: '', score: 0, proportionScore: 0 },
  ]);

  const [accidentHistory, setAccidentHistory] = useState<AssessmentDataItem[]>([
    { key: 'archiveAccidents', item: '档案登记的出险次数', details: '', score: 0, proportionScore: 0 },
    { key: 'afterSalesScore', item: '售后服务中的分值', details: '', score: 0, proportionScore: 0 },
  ]);

  const [drivingBehavior, setDrivingBehavior] = useState<AssessmentDataItem[]>([
    { key: 'laneDeparture', item: '百公里车道偏离次数', details: '', score: 0, proportionScore: 0 },
    { key: 'forwardCollision', item: '百公里前向碰撞预警次数', details: '', score: 0, proportionScore: 0 },
    { key: 'harshAcceleration', item: '百公里急加减速次数', details: '', score: 0, proportionScore: 0 },
    { key: 'emergencyBraking', item: '百公里紧急制动次数', details: '', score: 0, proportionScore: 0 },
  ]);

  const [drivingFocus, setDrivingFocus] = useState<AssessmentDataItem[]>([
    { key: 'fatigueDriving', item: '百公里疲劳驾驶次数', details: '', score: 0, proportionScore: 0 },
    { key: 'forwardCollisionWarning', item: '百公里前向碰撞预警次数', details: '', score: 0, proportionScore: 0 },
    { key: 'smoking', item: '百公里抽烟次数', details: '', score: 0, proportionScore: 0 },
  ]);

  // 将 OssEntity 数组转换为 UploadFile 数组（用于展示）
  const getDisplayArrFromOssEntities = (ossEntities: OssEntity[] | null): UploadFile[] => {
    if (!ossEntities || ossEntities.length === 0) return [];
    return ossEntities.map((entity: OssEntity) => ({
      uid: entity.ossKey as string,
      name: entity.ossKey as string,
      url: entity.ossUrl as string,
      thumbUrl: entity.ossUrl as string,
      status: 'done' as const,
    }));
  };

  // 将文件列表转换为逗号分隔的 ossKey 字符串（用于提交）
  const convertFileListToKeys = (fileList: UploadFile[]) => {
    if (!fileList || fileList.length === 0) return '';
    return fileList
      .map((file: any) => {
        // 如果是新上传的文件，从 response 中获取 ossKey
        if (file.response?.data) {
          return file.response.data.key;
        }
        // 如果是已存在的文件，从原始数据中获取 ossKey
        if (file.uid) {
          return file.uid;
        }
        // 如果都没有，尝试从 url 解析（备用方案）
        return file.url?.split('/').pop() || '';
      })
      .filter((key: string) => key !== '')
      .join(',');
  };


  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setFileLists({ idCard: [], driverLicense: [], photo: [] });
      setDrivingSkills([]);
      setWorkHistory([]);
      setLatestAssessmentLevel('');
      // Reset assessment tables data if needed
    } else if (initialValues) {
      // 处理日期字段，将字符串格式转换为 dayjs 对象
      const formattedValues = {
        ...initialValues,
        birthday: initialValues.birthday ? dayjs(initialValues.birthday) : undefined,
        lastWorkDate: initialValues.lastWorkDate ? dayjs(initialValues.lastWorkDate) : undefined,
      };

      form.setFieldsValue(formattedValues);

      // Initialize file lists from initialValues
      // 初始化图片文件列表 - 从 OssEntity[] 转换为 UploadFile[]
      const newFileLists: iImgFileDisplay = {
        idCard: [],
        driverLicense: [],
        photo: [],
      };

      // 处理身份证图片 idCardFileUrl -> idCard
      if (initialValues.idCardFileUrl) {
        newFileLists.idCard = getDisplayArrFromOssEntities(initialValues.idCardFileUrl);
      }

      // 处理驾驶证图片 driveLicenseFileUrl -> driverLicense
      if (initialValues.driveLicenseFileUrl) {
        newFileLists.driverLicense = getDisplayArrFromOssEntities(initialValues.driveLicenseFileUrl);
      }

      // 处理司机照片 driverImageFileUrl -> photo
      if (initialValues.driverImageFileUrl) {
        newFileLists.photo = getDisplayArrFromOssEntities(initialValues.driverImageFileUrl);
      }

      setFileLists(newFileLists);

      setDrivingSkills(initialValues.xxyyDrivingSkills || []);
      setWorkHistory(initialValues.xxyyWorkHistory || []);
      setLatestAssessmentLevel(initialValues.xxyyLatestAssessmentLevel || '');
      setIndividualInfo(initialValues.xxyyIndividualInfo || individualInfo);
      setDrivingViolations(initialValues.xxyyDrivingViolations || drivingViolations);
      setAccidentHistory(initialValues.xxyyAccidentHistory || accidentHistory);
      setDrivingBehavior(initialValues.xxyyDrivingBehavior || drivingBehavior);
      setDrivingFocus(initialValues.xxyyDrivingFocus || drivingFocus);
    }
  }, [visible, initialValues, form]);


  // 确认按钮点击事件
  const handleOk = () => {
    form.validateFields().then(values => {
      // 处理文件上传并获取最终的 URLs
      const finalValues = {
        ...values,
        // 将日期对象转换回字符串格式 YYYY-MM-DD
        birthday: values.birthday ? dayjs(values.birthday).format('YYYY-MM-DD') : undefined,
        // 2. xxxFile: 逗号分隔的 key 字符串（用于后端存储）
        idCardFile: convertFileListToKeys(fileLists.idCard),
        drivingLicenseFile: convertFileListToKeys(fileLists.driverLicense),
        driverImageFile: convertFileListToKeys(fileLists.photo)

      };

      delete finalValues.lastWorkDate;
      delete finalValues.idCardFileUrl;
      delete finalValues.driveLicenseFileUrl;
      delete finalValues.driverImageFileUrl;

      //删除所有xxyy开头的字段（所有xxyy开头的都是还没有对应关系的临时字段）
      Object.keys(finalValues).forEach(key => {
        if (key.startsWith('xxyy')) {
          delete finalValues[key];
        }
      });


      onOk(finalValues);
    });
  };

  const handleUploadChange = (info: any, name: string) => {
    setFileLists({ ...fileLists, [name]: info.fileList });
  };

  return (
    <Modal
      title={isViewMode ? '查看司机详情' : '编辑司机详情'}
      visible={visible}
      onCancel={onCancel}
      width="80%"
      footer={
        isViewMode ? (
          <Button onClick={onCancel}>关闭</Button>
        ) : (
          <>
            <Button onClick={onCancel} disabled={confirmLoading}>取消</Button>
            <Button type="primary" onClick={handleOk} loading={confirmLoading}>
              提交
            </Button>
          </>
        )
      }
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Form form={form} layout="vertical" disabled={isViewMode}>
        <Divider orientation="left">司机基本信息</Divider>
        <DriverBasicInfoForm
          isViewMode={isViewMode}
          fileLists={fileLists}
          handleUploadChange={handleUploadChange}
        />
        <Divider orientation="left">核心技能基本信息</Divider>
        <DriverCoreSkillsForm />
      </Form>

      <DrivingSkillsTable
        isViewMode={isViewMode}
        driverId={initialValues?.driverId}
      />
      <Divider orientation="left">工作履历基本信息</Divider>
      <WorkHistorySection
        isViewMode={isViewMode}
        driverId={initialValues?.driverId}
      />

      <Divider orientation="left">安全评估基本信息</Divider>
      <SafetyAssessmentSection
        isViewMode={isViewMode}
        driverId={initialValues?.driverId}
      />
    </Modal>
  );
};

export default DriverDetailModal;
